import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ArrowRight, Plus, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const HeroHotspot = ({ x, y, title, description }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div 
      className="absolute z-50 group pointer-events-auto" 
      style={{ top: `${y}%`, left: `${x}%` }}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onClick={() => setIsOpen(!isOpen)}
    >
      <button 
        className={`
          w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-300 relative z-50
          ${isOpen ? 'bg-accent text-white rotate-45 scale-110' : 'bg-white/20 backdrop-blur-sm text-white hover:bg-accent hover:scale-110'}
        `}
      >
        <Plus className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.9 }}
            className="absolute left-1/2 -translate-x-1/2 mt-4 w-64 md:w-72 bg-black/90 backdrop-blur-md border border-white/20 p-4 rounded-xl shadow-2xl z-50"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-accent/20 rounded-lg">
                <Info className="w-4 h-4 text-accent" />
              </div>
              <div>
                <h4 className="font-bold text-white text-sm uppercase tracking-wide mb-1">{title}</h4>
                <p className="text-gray-300 text-xs leading-relaxed">{description}</p>
              </div>
            </div>
            {/* Arrow */}
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-black/90 border-t border-l border-white/20 rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const BrutalistHero = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo(
      titleRef.current.children,
      { y: 100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, stagger: 0.2 }
    )
      .fromTo(
        subtitleRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.5'
      )
      .fromTo(
        buttonRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.6'
      );
  }, []);

  const hotspots = [
    { x: 30, y: 40, title: 'Вы выбираете размер и срок', description: 'Интеллектуальная система подбора. Точность до сантиметра. Оплата только за используемое пространство.' },
    { x: 65, y: 35, title: 'Круглосуточный мониторинг', description: 'Датчики движения, температуры и влажности. Полная изоляция.' },
    { x: 50, y: 60, title: 'Доступ 24/7 по личному коду', description: 'Вход через смартфон. Никаких ключей, никаких встреч с менеджерами.' }
  ];

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background text-foreground"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="w-full h-full bg-cover bg-center transition-transform duration-[20s] hover:scale-105"
          style={{
             backgroundImage: "url('/images/main-hero.png')"
          }}
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Interactive Hotspots */}
      <div className="absolute inset-0 hidden md:block z-20 pointer-events-none">
        <div className="w-full h-full relative">
          {hotspots.map((spot, i) => (
            <HeroHotspot key={i} {...spot} />
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full text-center md:text-left">
        <div ref={titleRef} className="flex flex-col gap-2 mb-8">
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-sans font-bold tracking-tighter text-white uppercase leading-none break-words">
            Контролируйте
          </h1>
          <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-serif italic text-accent leading-none ml-0 md:ml-12 break-words">
            Систему.
          </h1>
        </div>

        <p
          ref={subtitleRef}
          className="max-w-xl text-lg md:text-xl font-mono text-gray-300 mb-10 leading-relaxed"
        >
          Аренда индивидуальных кладовок 24/7 с цифровым доступом и
          видеонаблюдением.
        </p>

        <div ref={buttonRef}>
          <Link
            to="/catalog"
            className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 text-lg font-mono uppercase tracking-widest hover:bg-accent/90 transition-all hover:scale-105 active:scale-95"
          >
            Найти бокс
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>

      <div className="absolute top-1/2 right-6 md:right-12 -translate-y-1/2 flex flex-col gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="w-1 h-1 bg-accent rounded-full animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
        ))}
      </div>
    </section>
  );
};

export default BrutalistHero;
